import { PlatformApiProvider } from '@crest/platform-web/api';
import { PlatformConfigurationRegistry } from '@crest/platform-configuration/registry';
import {
  PlatformBootstrapProvider,
  usePlatformBootstrap,
} from '@crest/platform-web/runtime';
import { PlatformShell, type PlatformNavigationItem } from '@crest/platform-web/shell';
import { PlatformThemeProvider, usePlatformTheme } from '@crest/platform-web/theme';
import { PlatformMediaAcquisitionProvider } from '@crest/platform-web/media-acquisition';
import { PlatformWebHostRegistryProvider } from '@crest/platform-web/host-runtime';
import type { PlatformWebSettingEditorProps } from '@crest/platform-web/module-runtime';
import { platformWebModuleContribution } from '@crest/platform-web/web-catalog';
import { projectsCopyValueSchema } from '@crest/module-projects/contracts/portal-settings';
import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import {
  platformWebInstalledModuleContributions,
  platformWebReleaseCatalogHash,
} from './platform-web-release-catalog.generated.js';
import {
  buildPlatformWebSettingEditorContributions,
  buildPlatformWebNavigation,
  getAvailablePlatformWebRoutes,
} from './platform-web-release-runtime.js';
import { loadCrestBrowserLaunchConfig } from './crest-auth-config.js';
import { CrestAuthProvider, CrestSessionGate, CrestSignOutButton, useCrestAuth } from './crest-auth-provider.js';
import { canAccessCommandCenter } from './command-center-access.js';
import { buildCrestAccountAccessSummary } from './crest-account-access.panel.js';
import { CrestAccountPasswordPage } from './crest-account-password.page.js';

const PlatformConfigurationPage = lazy(() => import('@crest/platform-web/configuration'));
const CommandCenterPage = lazy(() => import('./command-center.page.js'));
const allWebModuleContributions = [
  platformWebModuleContribution,
  ...platformWebInstalledModuleContributions,
] as const;
const installedRoutes = allWebModuleContributions.flatMap(({ routes }) => routes);
const installedSettingEditors = buildPlatformWebSettingEditorContributions(
  allWebModuleContributions,
);
const lazyRouteComponents = new Map(
  installedRoutes.map((route) => [route.id, lazy(route.load)] as const),
);
const lazySettingEditorComponents = new Map<string, ComponentType<PlatformWebSettingEditorProps>>(
  [...installedSettingEditors].map(([settingKey, editor]) => (
    [settingKey, lazy(editor.load)] as const
  )),
);
const configurationRegistry = new PlatformConfigurationRegistry([
  ...allWebModuleContributions.flatMap(
    ({ settingRegistrations }) => settingRegistrations,
  ),
]);

function ConfiguredApplication() {
  const bootstrap = usePlatformBootstrap();
  const { branding, copy } = usePlatformTheme();
  const configurationAvailable = bootstrap.permissions.includes('platform.configuration.manage');
  const availableRoutes = getAvailablePlatformWebRoutes(installedRoutes, {
    modules: bootstrap.modules,
    permissions: bootstrap.permissions,
    configurationValues: bootstrap.configuration.values,
  });
  const navigation: PlatformNavigationItem[] = buildPlatformWebNavigation(
    availableRoutes,
    bootstrap.configuration.values,
  );
  const commandCenterAvailable = canAccessCommandCenter(bootstrap.permissions);
  if (commandCenterAvailable) {
    navigation.unshift({
      id: 'platform.navigation.command-center',
      label: 'Command',
      to: '/',
      groupId: 'command',
      groupLabel: copy.navigationGroupCommand,
    });
  }
  if (configurationAvailable) {
    navigation.push({
      id: 'settings',
      label: copy.settingsLabel,
      to: '/settings/configuration',
      groupId: 'admin',
      groupLabel: copy.navigationGroupAdmin,
    });
  }
  const projectsNavigation = navigation.find(({ id }) => id === 'projects.navigation.projects');
  const projectsCopy = projectsNavigation === undefined
    ? null
    : projectsCopyValueSchema.parse(bootstrap.configuration.values['projects.copy.default']);
  const defaultPath = commandCenterAvailable
    ? '/'
    : (availableRoutes[0]?.path ??
      (configurationAvailable ? '/settings/configuration' : null));

  return (
    <PlatformWebHostRegistryProvider
      moduleContributions={allWebModuleContributions}
    >
      <PlatformShell
        navigation={navigation}
        accountActions={(
          <>
            <Link className="crest-account-action" to="/account/password">Account</Link>
            <CrestSignOutButton />
          </>
        )}
        {...(projectsNavigation === undefined || projectsCopy === null ? {} : {
          search: {
            action: projectsNavigation.to,
            label: projectsCopy.searchLabel,
            placeholder: projectsCopy.searchPlaceholder,
          },
        })}
      >
        <Suspense fallback={<p className="crest-loading-state" aria-live="polite">{copy.loadingLabel}</p>}>
          <Routes>
          <Route path="/account/password" element={<CrestAccountPasswordPage accessSummary={
            buildCrestAccountAccessSummary(branding.companyDisplayName, availableRoutes)
          } />} />
          {commandCenterAvailable ? <Route path="/" element={<CommandCenterPage availableRoutes={availableRoutes} />} /> : null}
          {availableRoutes.map((route) => {
            const Component = lazyRouteComponents.get(route.id);
            if (Component === undefined) throw new Error(`Missing lazy route: ${route.id}`);
            return <Route key={route.id} path={route.path} element={<Component />} />;
          })}
          {availableRoutes.some(({ path }) => path === '/dispatch') ? (
            <Route path="/scheduling" element={<Navigate replace to="/dispatch" />} />
          ) : null}
          {configurationAvailable ? (
            <Route
              path="/settings/configuration"
              element={(
                <PlatformConfigurationPage
                  registry={configurationRegistry}
                  settingEditors={lazySettingEditorComponents}
                />
              )}
            />
          ) : null}
          <Route
            path="*"
            element={
              defaultPath !== null ? (
                <Navigate replace to={defaultPath} />
              ) : (
                <p role="status">{copy.noAvailableModulesLabel}</p>
              )
            }
          />
          </Routes>
        </Suspense>
      </PlatformShell>
    </PlatformWebHostRegistryProvider>
  );
}

function AuthenticatedApplication() {
  const auth = useCrestAuth();
  return (
    <PlatformApiProvider authentication={{ getAccessToken: auth.getAccessToken, tenantId: auth.tenantId }}>
      <BrowserRouter>
        <PlatformBootstrapProvider
          webClientVersion={__CREST_WEB_CLIENT_VERSION__}
          releaseCatalogHash={platformWebReleaseCatalogHash}
        >
          <PlatformMediaAcquisitionProvider>
            <PlatformThemeProvider>
              <ConfiguredApplication />
            </PlatformThemeProvider>
          </PlatformMediaAcquisitionProvider>
        </PlatformBootstrapProvider>
      </BrowserRouter>
    </PlatformApiProvider>
  );
}

export function App() {
  const config = loadCrestBrowserLaunchConfig();
  return (
    <CrestAuthProvider config={config}>
      <CrestSessionGate>
        <AuthenticatedApplication />
      </CrestSessionGate>
    </CrestAuthProvider>
  );
}
